import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Mail, PanelLeft, Send, X } from "lucide-react";
import { useAuth } from "./AuthGate";
import MenuIconTooltip from "./MenuIconTooltip";
import {
  getBlasonByIndex,
  getRoturierBlason,
} from "../constants/radiantDayBlasons";
import {
  DISPLAYED_PORTRAIT_EVENT,
  readRadiantDayDisplayedPortrait,
} from "../utils/radiantDayTitlesPreference";
import vocabulariumBlason from "../assets/vocabularium_blason.png";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import { useFloatingWindowLayer } from "../utils/floatingWindows";
import { FRAME_LAUNCHER_TOOLTIPS } from "../constants/menuTooltips";
import { useAccountTextScale } from "../constants/textScalePreference";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import {
  ACCOUNT_PURGED_EVENT,
  PRESENCE_CHANGED_EVENT,
  fetchMessagingBootstrap,
  readAdminAppearOffline,
  writeAdminAppearOffline,
  fetchThreadMessages,
  formatMessageStamp,
  lastReceivedLabel,
  markThreadRead,
  openMessagingSocket,
  postMessagingAppearance,
  postThreadMessage,
  readMailWindowState,
  visibleUnreadForContact,
  visibleUnreadTotalForViewer,
  writeMailWindowState,
} from "../utils/kingdomMessaging";
import { hasUnknownOnlinePlayer } from "../multiuser/messagingLogic";
import "./VocabTooltip.css";
import "./KingdomMessaging.css";

function pickBlasonSrc(index) {
  return (getBlasonByIndex(index) || getRoturierBlason())?.imageSrc || "";
}

function defaultSelectedThread(contacts, storedId) {
  const list = Array.isArray(contacts) ? contacts : [];
  if (storedId && list.some((item) => item.threadId === storedId)) return storedId;
  const unread = list.find((item) => Number(item.unread) > 0);
  if (unread) return unread.threadId;
  const conversation = list.find((item) => item.kind === "conversation");
  return conversation?.threadId || list[0]?.threadId || "";
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const MAIL_TEXT_SCALE_GROW = 20;

function mailWindowMetrics(box, frameGrow = 0) {
  const scale = box.scale > 0 ? box.scale : 1;
  const frameWidth = box.width / scale;
  const frameHeight = box.height / scale;
  const grow = frameGrow > 0 ? frameGrow / scale : 0;
  const designWidth = frameWidth * 0.45 + grow;
  const designHeight = frameHeight * 0.48 + grow;
  return {
    scale,
    designWidth,
    designHeight,
    visualWidth: designWidth * scale,
    visualHeight: designHeight * scale,
  };
}

function isReadonlyKingdomThread(kind) {
  return kind === "official" || kind === "news";
}

function isOfficialMailMessage(message) {
  return (
    message?.kind === "official" ||
    message?.kind === "news" ||
    message?.senderRole === "official"
  );
}

function AdminPresenceMenu({ hidden, onChange }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  return (
    <div
      ref={rootRef}
      className={`kingdom-mail__presence-menu${
        menuOpen ? " kingdom-mail__presence-menu--open" : ""
      }`}
    >
      <button
        type="button"
        className="kingdom-mail__presence-menu-trigger"
        aria-label="Visibilité dans le Royaume"
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((value) => !value)}
      >
        <span>{hidden ? "hors ligne" : "en ligne"}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {menuOpen ? (
        <ul className="kingdom-mail__presence-menu-list" role="listbox">
          {[
            ["en ligne", false],
            ["hors ligne", true],
          ].map(([label, value]) => (
            <li key={label}>
              <button
                type="button"
                role="option"
                aria-selected={hidden === value}
                onClick={() => {
                  onChange(value);
                  setMenuOpen(false);
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function MailScrollbar({ trackRef, scrollbar, onTrackPointerDown, onThumbPointerDown }) {
  return (
    <div
      ref={trackRef}
      className={`kingdom-mail__scrollbar${
        scrollbar.visible ? "" : " kingdom-mail__scrollbar--hidden"
      }`}
      aria-hidden="true"
      onPointerDown={onTrackPointerDown}
    >
      {scrollbar.visible ? (
        <div
          className="kingdom-mail__scrollbar-thumb"
          style={{
            height: `${scrollbar.height}px`,
            transform: `translateY(${scrollbar.top}px)`,
          }}
          onPointerDown={onThumbPointerDown}
        />
      ) : null}
    </div>
  );
}

function readDisplayedBlasonIndex(playerName) {
  const cached = readRadiantDayDisplayedPortrait(playerName || "");
  const index = Number(cached?.blasonIndex);
  return Number.isInteger(index) && index >= 0 ? index : null;
}

function mergeContactsPreserveBlason(previousContacts, nextContacts) {
  const previousByThread = new Map(
    (previousContacts || []).map((item) => [item.threadId, item])
  );
  return (nextContacts || []).map((contact) => {
    const previous = previousByThread.get(contact.threadId);
    const incoming = Number(contact.blasonIndex);
    const previousIndex = Number(previous?.blasonIndex);
    if (
      previous &&
      Number.isInteger(previousIndex) &&
      previousIndex > 0 &&
      incoming === 0
    ) {
      return { ...contact, blasonIndex: previousIndex };
    }
    return contact;
  });
}

function readFrameBox() {
  const frame = document.querySelector(".custom-pattern");
  if (!frame) return { left: 0, top: 0, width: 0, height: 0, scale: 1 };
  const rect = frame.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    scale: getAppScale(),
  };
}

export default function KingdomMessaging() {
  const { user } = useAuth();
  const [textScale] = useAccountTextScale(user?.sub || "");
  const mailGrow = textScale > 1 ? MAIL_TEXT_SCALE_GROW : 0;
  const mailGrowRef = useRef(mailGrow);
  mailGrowRef.current = mailGrow;
  const messagesRef = useRef(null);
  const peopleRef = useRef(null);
  const composerRef = useRef(null);
  const dragRef = useRef(null);
  const typingTimerRef = useRef(null);
  const socketRef = useRef(null);
  const openRef = useRef(false);
  const selectedRef = useRef("");
  const posRef = useRef({ leftRatio: 0.5, topRatio: 0.5 });
  const bootstrapRef = useRef(null);
  const directoryRefreshRef = useRef(false);
  const adminRef = useRef(false);
  const userIdRef = useRef("");

  const stored = useMemo(() => readMailWindowState(), []);
  const [open, setOpen] = useState(false);
  const layer = useFloatingWindowLayer(open);
  const [listOpen, setListOpen] = useState(stored.listOpen !== false);
  const [listTipOff, setListTipOff] = useState(false);
  const [leftRatio, setLeftRatio] = useState(
    Number.isFinite(stored.leftRatio) ? stored.leftRatio : 0.5
  );
  const [topRatio, setTopRatio] = useState(
    Number.isFinite(stored.topRatio) ? stored.topRatio : 0.5
  );
  const [dragging, setDragging] = useState(false);
  const [bootstrap, setBootstrap] = useState(null);
  const [selectedThreadId, setSelectedThreadId] = useState(stored.selectedThreadId || "");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(null);
  const [frameBox, setFrameBox] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    scale: 1,
  });
  const [blasonIndex, setBlasonIndex] = useState(() =>
    readDisplayedBlasonIndex(user?.displayName)
  );
  const [appearOffline, setAppearOffline] = useState(() =>
    readAdminAppearOffline(user?.sub)
  );
  const appearOfflineRef = useRef(appearOffline);

  adminRef.current = Boolean(user?.admin);
  userIdRef.current = user?.sub || "";
  appearOfflineRef.current = appearOffline;

  useEffect(() => {
    if (!user?.admin || !user?.sub) {
      setAppearOffline(false);
      return;
    }
    setAppearOffline(readAdminAppearOffline(user.sub));
  }, [user?.admin, user?.sub]);

  openRef.current = open;
  selectedRef.current = selectedThreadId;
  posRef.current = { leftRatio, topRatio };
  bootstrapRef.current = bootstrap;

  const handlersRef = useRef({});

  const {
    trackRef: messagesTrackRef,
    scrollbar: messagesScrollbar,
    updateScrollbar: updateMessagesScrollbar,
    handleThumbPointerDown: handleMessagesThumbPointerDown,
    handleTrackPointerDown: handleMessagesTrackPointerDown,
  } = useCustomScrollbar(messagesRef);
  const {
    trackRef: peopleTrackRef,
    scrollbar: peopleScrollbar,
    updateScrollbar: updatePeopleScrollbar,
    handleThumbPointerDown: handlePeopleThumbPointerDown,
    handleTrackPointerDown: handlePeopleTrackPointerDown,
  } = useCustomScrollbar(peopleRef);
  const {
    trackRef: composerTrackRef,
    scrollbar: composerScrollbar,
    updateScrollbar: updateComposerScrollbar,
    handleThumbPointerDown: handleComposerThumbPointerDown,
    handleTrackPointerDown: handleComposerTrackPointerDown,
  } = useCustomScrollbar(composerRef, { minThumbHeight: 18 });

  const contacts = bootstrap?.contacts || [];
  const unreadTotal = visibleUnreadTotalForViewer({
    isAdmin: Boolean(bootstrap?.me?.admin),
    contacts,
    windowOpen: open,
    selectedThreadId,
  });
  const selected = contacts.find((item) => item.threadId === selectedThreadId) || null;
  const canCompose =
    selected?.kind === "conversation" &&
    (bootstrap?.me?.admin || selected?.canSend !== false);

  const statusText = useMemo(() => {
    if (
      typing?.isTyping &&
      typing.threadId === selectedThreadId &&
      typing.displayName
    ) {
      return `${typing.displayName} est en train d'écrire`;
    }
    return lastReceivedLabel(selected?.lastInboundAt) || "";
  }, [typing, selectedThreadId, selected?.lastInboundAt]);

  const applyOnline = useCallback((onlineSubs) => {
    const online = new Set(onlineSubs || []);
    setBootstrap((current) => {
      if (!current) return current;
      return {
        ...current,
        admin: current.admin
          ? { ...current.admin, online: online.has(current.admin.sub) }
          : current.admin,
        contacts: (current.contacts || []).map((contact) => ({
          ...contact,
          online: contact.peerSub ? online.has(contact.peerSub) : contact.online,
        })),
      };
    });
    window.dispatchEvent(new CustomEvent(PRESENCE_CHANGED_EVENT));
    const current = bootstrapRef.current;
    if (
      current?.me?.admin &&
      hasUnknownOnlinePlayer(onlineSubs, {
        contacts: current.contacts,
        meSub: current.me.sub,
        adminSub: current.admin?.sub,
      })
    ) {
      handlersRef.current.refreshDirectory?.();
    }
  }, []);

  const applyAppearance = useCallback((payload) => {
    const playerSub = String(payload?.playerSub || "");
    const nextIndex = Number(payload?.blasonIndex);
    if (!playerSub || !Number.isInteger(nextIndex) || nextIndex < 0) return;
    setBootstrap((current) => {
      if (!current) return current;
      return {
        ...current,
        admin:
          current.admin?.sub === playerSub
            ? {
                ...current.admin,
                blasonIndex: nextIndex,
                displayName: payload.displayName || current.admin.displayName,
              }
            : current.admin,
        contacts: (current.contacts || []).map((contact) => {
          const matches =
            contact.peerSub === playerSub || contact.playerSub === playerSub;
          if (!matches || isReadonlyKingdomThread(contact.kind)) return contact;
          return {
            ...contact,
            blasonIndex: nextIndex,
            name: payload.displayName || contact.name,
          };
        }),
      };
    });
  }, []);

  const applyBootstrap = useCallback((payload, preferThreadId = selectedRef.current) => {
    if (!payload) return;
    setBootstrap((current) => ({
      ...payload,
      contacts: mergeContactsPreserveBlason(current?.contacts, payload.contacts),
    }));
    setSelectedThreadId((current) =>
      defaultSelectedThread(payload.contacts, preferThreadId || current)
    );
  }, []);

  const refreshBootstrap = useCallback(async (options = {}) => {
    const payload = await fetchMessagingBootstrap(blasonIndex, options);
    applyBootstrap(payload);
    return payload;
  }, [applyBootstrap, blasonIndex]);

  const refreshDirectory = useCallback(() => {
    if (directoryRefreshRef.current) return;
    directoryRefreshRef.current = true;
    refreshBootstrap({ fresh: true })
      .catch(() => {})
      .finally(() => {
        directoryRefreshRef.current = false;
      });
  }, [refreshBootstrap]);

  const loadMessages = useCallback(async (threadId) => {
    if (!threadId) {
      setMessages([]);
      return;
    }
    const result = await fetchThreadMessages(threadId);
    if (selectedRef.current !== threadId) return;
    setMessages(Array.isArray(result.messages) ? result.messages : []);
  }, []);

  const markReadIfVisible = useCallback(async (threadId) => {
    if (!threadId || !openRef.current) return;
    if (adminRef.current && appearOfflineRef.current) return;
    const result = await markThreadRead(threadId);
    setBootstrap((current) =>
      current
        ? {
            ...current,
            contacts: mergeContactsPreserveBlason(
              current.contacts,
              result.contacts || current.contacts
            ),
            unreadTotal: result.unreadTotal,
          }
        : current
    );
  }, []);

  const syncAppearOffline = useCallback((hidden) => {
    if (!adminRef.current || !userIdRef.current) return;
    const next = hidden === true;
    writeAdminAppearOffline(userIdRef.current, next);
    setAppearOffline(next);
  }, []);

  handlersRef.current = {
    applyOnline,
    applyAppearance,
    refreshBootstrap,
    refreshDirectory,
    loadMessages,
    markReadIfVisible,
    syncAppearOffline,
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (Number.isInteger(blasonIndex) && blasonIndex >= 0) {
          await postMessagingAppearance(blasonIndex).catch(() => null);
        }
        const payload = await fetchMessagingBootstrap(blasonIndex);
        if (!cancelled) applyBootstrap(payload);
      } catch (error) {
        console.warn("Messagerie :", error.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyBootstrap, blasonIndex, user?.sub]);

  useEffect(() => {
    if (!open || !bootstrap?.me?.admin) return;
    refreshDirectory();
  }, [open, bootstrap?.me?.admin, refreshDirectory]);

  useEffect(() => {
    const onAccountPurged = (event) => {
      const userId = String(event.detail?.userId || "").trim();
      if (userId) {
        setBootstrap((current) => {
          if (!current) return current;
          return {
            ...current,
            contacts: (current.contacts || []).filter(
              (contact) => contact.playerSub !== userId && contact.peerSub !== userId
            ),
          };
        });
        setSelectedThreadId((current) => {
          if (!current) return current;
          if (
            current === `conv:${userId}` ||
            current === `official:${userId}` ||
            current === `news:${userId}`
          ) {
            return "";
          }
          return current;
        });
      }
      handlersRef.current.refreshBootstrap?.().catch(() => {});
    };
    window.addEventListener(ACCOUNT_PURGED_EVENT, onAccountPurged);
    return () => window.removeEventListener(ACCOUNT_PURGED_EVENT, onAccountPurged);
  }, []);

  useEffect(() => {
    const index = readDisplayedBlasonIndex(user?.displayName);
    if (index != null) setBlasonIndex(index);
  }, [user?.displayName]);

  useEffect(() => {
    const sync = (event) => {
      const index = Number(event?.detail?.blasonIndex);
      if (Number.isInteger(index) && index >= 0) {
        setBlasonIndex(index);
        return;
      }
      const cached = readDisplayedBlasonIndex(user?.displayName);
      if (cached != null) setBlasonIndex(cached);
    };
    window.addEventListener(DISPLAYED_PORTRAIT_EVENT, sync);
    return () => window.removeEventListener(DISPLAYED_PORTRAIT_EVENT, sync);
  }, [user?.displayName]);

  useEffect(() => {
    if (!user?.sub) return undefined;
    const abort = new AbortController();
    let retry;
    let delay = 1000;
    let socket;

    const connect = async () => {
      if (abort.signal.aborted) return;
      try {
        socket = await openMessagingSocket({
          signal: abort.signal,
          appearOffline:
            adminRef.current && readAdminAppearOffline(userIdRef.current),
        });
        if (abort.signal.aborted) {
          return;
        }
        socketRef.current = socket;
        socket.addEventListener("open", () => {
          delay = 1000;
        });
        socket.addEventListener("close", () => {
          if (abort.signal.aborted) return;
          retry = setTimeout(connect, delay);
          delay = Math.min(delay * 2, 15000);
        });
        socket.addEventListener("message", (event) => {
          let payload;
          try {
            payload = JSON.parse(event.data);
          } catch {
            return;
          }
          const handlers = handlersRef.current;
          if (payload.type === "hello") {
            if (payload.admin === true && typeof payload.appearOffline === "boolean") {
              handlers.syncAppearOffline?.(payload.appearOffline);
            }
            handlers.refreshBootstrap?.().catch(() => {});
            return;
          }
          if (payload.type === "read") {
            const threadId = String(payload.threadId || "");
            const readAt = String(payload.readAt || "");
            if (threadId && readAt && selectedRef.current === threadId) {
              setMessages((current) =>
                current.map((message) =>
                  message.mine &&
                  !isOfficialMailMessage(message) &&
                  message.createdAt &&
                  message.createdAt <= readAt
                    ? { ...message, read: true }
                    : message
                )
              );
            }
            return;
          }
          if (payload.type === "appear-offline") {
            handlers.syncAppearOffline?.(payload.hidden === true);
            return;
          }
          if (payload.type === "directory") {
            handlers.refreshDirectory?.();
            return;
          }
          if (payload.type === "presence") {
            handlers.applyOnline?.(payload.online || []);
            return;
          }
          if (payload.type === "appearance") {
            handlers.applyAppearance?.(payload);
            return;
          }
          if (payload.type === "typing") {
            setTyping(
              payload.isTyping
                ? {
                    threadId: payload.threadId,
                    displayName: payload.displayName || "Quelqu'un",
                    isTyping: true,
                  }
                : null
            );
            return;
          }
          if (payload.type === "permission") {
            setBootstrap((current) =>
              current
                ? {
                    ...current,
                    me: { ...current.me, canSend: payload.canSend !== false },
                    contacts: (current.contacts || []).map((contact) =>
                      contact.kind === "conversation"
                        ? { ...contact, canSend: payload.canSend !== false }
                        : contact
                    ),
                  }
                : current
            );
            return;
          }
          if (
            payload.type === "official" ||
            payload.type === "official-update" ||
            payload.type === "official-delete" ||
            payload.type === "message"
          ) {
            handlers
              .refreshBootstrap?.()
              .then(() => {
                if (!openRef.current) return;
                const incomingThreadId = String(payload.threadId || "");
                const selectedId = selectedRef.current;
                if (!incomingThreadId || incomingThreadId !== selectedId) return;
                handlers.loadMessages?.(incomingThreadId);
                handlers.markReadIfVisible?.(incomingThreadId);
              })
              .catch(() => {});
          }
        });
      } catch (error) {
        if (abort.signal.aborted || error?.name === "AbortError") return;
        retry = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 15000);
      }
    };

    connect();
    return () => {
      abort.abort();
      clearTimeout(retry);
      const openSocket = socketRef.current;
      socketRef.current = null;
      if (!openSocket) return;
      if (openSocket.readyState === WebSocket.OPEN) {
        openSocket.close();
        return;
      }
      if (openSocket.readyState === WebSocket.CONNECTING) {
        openSocket.addEventListener("open", () => openSocket.close(), { once: true });
      }
    };
  }, [user?.sub]);

  useEffect(() => {
    if (!open || !selectedThreadId) return;
    loadMessages(selectedThreadId).catch(() => {});
    markReadIfVisible(selectedThreadId).catch(() => {});
    writeMailWindowState({ selectedThreadId });
  }, [loadMessages, markReadIfVisible, open, selectedThreadId]);

  useLayoutEffect(() => {
    const update = () => setFrameBox(readFrameBox());
    update();
    const frame = document.querySelector(".custom-pattern");
    const observer =
      frame && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(update)
        : null;
    if (frame && observer) observer.observe(frame);
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, [open]);

  useLayoutEffect(() => {
    const node = messagesRef.current;
    if (!node) return;
    node.scrollTop = isReadonlyKingdomThread(selected?.kind) ? 0 : node.scrollHeight;
    updateMessagesScrollbar();
  }, [messages, open, selectedThreadId, selected?.kind, updateMessagesScrollbar]);

  useEffect(() => {
    updatePeopleScrollbar();
  }, [contacts.length, listOpen, open, updatePeopleScrollbar]);

  useLayoutEffect(() => {
    updateComposerScrollbar();
  }, [draft, canCompose, open, updateComposerScrollbar]);

  useEffect(() => {
    if (!open) return undefined;
    const nodes = [messagesRef.current, peopleRef.current].filter(Boolean);
    if (!nodes.length || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      updateMessagesScrollbar();
      updatePeopleScrollbar();
    });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [open, listOpen, messages.length, contacts.length, updateMessagesScrollbar, updatePeopleScrollbar]);

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const box = readFrameBox();
      const size = mailWindowMetrics(box, mailGrowRef.current);
      const maxLeft = Math.max(box.width - size.visualWidth, 0);
      const maxTop = Math.max(box.height - size.visualHeight, 0);
      const left = clamp(event.clientX - drag.offsetX - box.left, 0, maxLeft);
      const top = clamp(event.clientY - drag.offsetY - box.top, 0, maxTop);
      const nextLeft = maxLeft ? left / maxLeft : 0;
      const nextTop = maxTop ? top / maxTop : 0;
      posRef.current = { leftRatio: nextLeft, topRatio: nextTop };
      setLeftRatio(nextLeft);
      setTopRatio(nextTop);
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setDragging(false);
      writeMailWindowState(posRef.current);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const scale = frameBox.scale || 1;
  const toggleStyle = {
    left: `${frameBox.left}px`,
    top: `${frameBox.top + frameBox.height / 2 + 39 * scale}px`,
  };

  const windowStyle = useMemo(() => {
    const size = mailWindowMetrics(frameBox, mailGrow);
    const maxLeft = Math.max(frameBox.width - size.visualWidth, 0);
    const maxTop = Math.max(frameBox.height - size.visualHeight, 0);
    return {
      width: `${size.designWidth}px`,
      height: `${size.designHeight}px`,
      minWidth: `${size.designWidth}px`,
      minHeight: `${size.designHeight}px`,
      left: `${frameBox.left + leftRatio * maxLeft}px`,
      top: `${frameBox.top + topRatio * maxTop}px`,
      transform: `scale(${size.scale})`,
      transformOrigin: "top left",
    };
  }, [frameBox, leftRatio, topRatio, mailGrow]);

  const startDrag = (event) => {
    if (event.target.closest("button")) return;
    const windowEl = event.currentTarget.parentElement;
    if (!windowEl) return;
    const rect = windowEl.getBoundingClientRect();
    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setDragging(true);
    event.preventDefault();
  };

  const chooseAppearOffline = (hidden) => {
    if (!user?.admin || !user?.sub) return;
    const next = hidden === true;
    writeAdminAppearOffline(user.sub, next);
    appearOfflineRef.current = next;
    setAppearOffline(next);
    const socket = socketRef.current;
    if (socket && socket.readyState === 1) {
      socket.send(JSON.stringify({ type: "appear-offline", hidden: next }));
    }
    if (!next && openRef.current && selectedRef.current) {
      markReadIfVisible(selectedRef.current).catch(() => {});
    }
  };

  const emitTyping = (isTyping) => {
    const socket = socketRef.current;
    const targetSub = selected?.peerSub;
    if (!socket || socket.readyState !== 1 || !selectedThreadId || !targetSub) return;
    socket.send(
      JSON.stringify({
        type: "typing",
        threadId: selectedThreadId,
        isTyping,
      })
    );
  };

  const handleDraftChange = (value) => {
    setDraft(value);
    emitTyping(Boolean(value.trim()));
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => emitTyping(false), 1600);
  };

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || !selectedThreadId || !canCompose || sending) return;
    setSending(true);
    try {
      emitTyping(false);
      const result = await postThreadMessage(selectedThreadId, body);
      setDraft("");
      setMessages((current) => {
        if (selectedRef.current !== selectedThreadId) return current;
        if (current.some((item) => item.id === result.message.id)) return current;
        return [...current, result.message];
      });
      if (result.contacts) {
        setBootstrap((current) =>
          current
            ? { ...current, contacts: result.contacts, unreadTotal: result.unreadTotal }
            : current
        );
      }
    } catch (error) {
      console.warn("Envoi message :", error.message);
    } finally {
      setSending(false);
    }
  };

  if (!user?.sub) return null;
  const portalRoot = getAppPortalRoot();
  if (!portalRoot) return null;

  const badgeLabel =
    unreadTotal > 99 ? "99+" : unreadTotal > 0 ? String(unreadTotal) : "";

  return createPortal(
    <div className="kingdom-mail">
      <MenuIconTooltip
        tooltip={FRAME_LAUNCHER_TOOLTIPS.mail}
        placement="below"
        portal
        className="kingdom-mail__tip"
        style={toggleStyle}
      >
        <button
          type="button"
          className="kingdom-mail__toggle"
          aria-label={FRAME_LAUNCHER_TOOLTIPS.mail}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="kingdom-mail__toggle-mark">
            <Mail className="kingdom-mail__toggle-icon" aria-hidden="true" />
            {badgeLabel ? (
              <span className="kingdom-mail__badge" aria-label={`${unreadTotal} non lus`}>
                {badgeLabel}
              </span>
            ) : null}
          </span>
        </button>
      </MenuIconTooltip>

      {open ? (
        <section
          className="kingdom-mail__window"
          style={{ ...windowStyle, zIndex: layer.zIndex }}
          aria-label="Messagerie du Royaume"
          onPointerDownCapture={layer.bringToFront}
        >
          <header
            className={`kingdom-mail__banner${dragging ? " kingdom-mail__banner--dragging" : ""}`}
            onPointerDown={startDrag}
          >
            <div className="kingdom-mail__banner-start">
            <span
              className={`vocab-tooltip-wrap${listTipOff ? " vocab-tooltip-wrap--off" : ""}`}
              onMouseLeave={() => setListTipOff(false)}
            >
              <button
                type="button"
                className="kingdom-mail__circle-btn"
                aria-label={
                  listOpen
                    ? "Donner toute la largeur à la conversation"
                    : "Réafficher la liste des joueurs"
                }
                aria-pressed={listOpen}
                onClick={(event) => {
                  const next = !listOpen;
                  setListOpen(next);
                  writeMailWindowState({ listOpen: next });
                  setListTipOff(true);
                  event.currentTarget.blur();
                }}
              >
                <PanelLeft aria-hidden="true" />
              </button>
              <span className="vocab-tooltip vocab-tooltip--below" role="tooltip">
                {listOpen
                  ? "Donner toute la largeur à la conversation"
                  : "Réafficher la liste des joueurs"}
              </span>
            </span>
            {user?.admin ? (
              <AdminPresenceMenu hidden={appearOffline} onChange={chooseAppearOffline} />
            ) : null}
            </div>
            <div className="kingdom-mail__title-block">
              <h2 className="kingdom-mail__title">Messagerie du Royaume</h2>
              <p className="kingdom-mail__status">{statusText}</p>
            </div>
            <button
              type="button"
              className="kingdom-mail__circle-btn"
              aria-label="Fermer la messagerie"
              onClick={() => setOpen(false)}
            >
              <X aria-hidden="true" />
            </button>
          </header>

          <div className="kingdom-mail__body">
            <aside
              className={`kingdom-mail__people-wrap${listOpen ? "" : " kingdom-mail__people-wrap--hidden"}`}
            >
              <div className="kingdom-mail__people" ref={peopleRef} onScroll={updatePeopleScrollbar}>
              {contacts.map((contact) => {
                const listUnread = visibleUnreadForContact(contact, {
                  windowOpen: open,
                  selectedThreadId,
                });
                return (
                <button
                  key={contact.threadId}
                  type="button"
                  className={`kingdom-mail__person${
                    contact.threadId === selectedThreadId ? " kingdom-mail__person--active" : ""
                  }`}
                  onClick={() => setSelectedThreadId(contact.threadId)}
                >
                  {isReadonlyKingdomThread(contact.kind) ? (
                    <img
                      className="kingdom-mail__official-mark"
                      src={`${process.env.PUBLIC_URL || ""}/favicon.svg`}
                      alt=""
                    />
                  ) : (
                    <img
                      className="kingdom-mail__blason"
                      src={pickBlasonSrc(contact.blasonIndex)}
                      alt=""
                    />
                  )}
                  <span className="kingdom-mail__person-name">{contact.name}</span>
                  {listUnread > 0 ? (
                    <span className="kingdom-mail__person-unread">{listUnread}</span>
                  ) : null}
                  {contact.kind === "conversation" ? (
                    <span
                      className={`kingdom-mail__presence${
                        contact.online ? " kingdom-mail__presence--online" : ""
                      }`}
                      aria-label={contact.online ? "en ligne" : "hors ligne"}
                    />
                  ) : null}
                </button>
                );
              })}
              </div>
              <MailScrollbar
                trackRef={peopleTrackRef}
                scrollbar={peopleScrollbar}
                onTrackPointerDown={handlePeopleTrackPointerDown}
                onThumbPointerDown={handlePeopleThumbPointerDown}
              />
            </aside>

            <div className="kingdom-mail__thread">
              <div
                className={`kingdom-mail__messages-wrap${
                  listOpen ? "" : " kingdom-mail__messages-wrap--full"
                }`}
              >
                <div
                  className="kingdom-mail__messages"
                  ref={messagesRef}
                  onScroll={updateMessagesScrollbar}
                >
                {messages.length ? (
                  (selected?.kind === "news"
                    ? [...messages].sort((a, b) =>
                        String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
                      )
                    : messages
                  ).map((message) => (
                    <article
                      key={message.id}
                      className={`kingdom-mail__msg ${
                        message.mine ? "kingdom-mail__msg--mine" : "kingdom-mail__msg--theirs"
                      }${isOfficialMailMessage(message) ? " kingdom-mail__msg--official" : ""}`}
                    >
                      <time className="kingdom-mail__msg-meta" dateTime={message.createdAt}>
                        {message.mine && message.read && !isOfficialMailMessage(message) ? (
                          <>
                            Lu
                            <span className="kingdom-mail__msg-read-dot" aria-hidden="true">
                              ·
                            </span>
                          </>
                        ) : null}
                        {formatMessageStamp(message.createdAt)}
                      </time>
                      <div className="kingdom-mail__bubble">
                        <p className="kingdom-mail__bubble-text">{message.body}</p>
                        {isOfficialMailMessage(message) ? (
                          <img
                            className="kingdom-mail__official-seal"
                            src={vocabulariumBlason}
                            alt=""
                          />
                        ) : null}
                      </div>
                    </article>
                  ))
                ) : (
                  <p className="kingdom-mail__empty">Aucun message pour le moment.</p>
                )}
                </div>
                <MailScrollbar
                  trackRef={messagesTrackRef}
                  scrollbar={messagesScrollbar}
                  onTrackPointerDown={handleMessagesTrackPointerDown}
                  onThumbPointerDown={handleMessagesThumbPointerDown}
                />
              </div>

              {canCompose ? (
                <form
                  className="kingdom-mail__composer"
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleSend();
                  }}
                >
                  <div
                    className={`kingdom-mail__input-wrap${
                      composerScrollbar.visible ? " kingdom-mail__input-wrap--scroll" : ""
                    }`}
                  >
                    <textarea
                      ref={composerRef}
                      className="kingdom-mail__input"
                      value={draft}
                      onChange={(event) => handleDraftChange(event.target.value)}
                      onScroll={updateComposerScrollbar}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          handleSend();
                        }
                      }}
                      rows={2}
                      maxLength={2000}
                      placeholder="Écrire un message…"
                      aria-label="Écrire un message"
                    />
                    <MailScrollbar
                      trackRef={composerTrackRef}
                      scrollbar={composerScrollbar}
                      onTrackPointerDown={handleComposerTrackPointerDown}
                      onThumbPointerDown={handleComposerThumbPointerDown}
                    />
                  </div>
                  <button
                    type="submit"
                    className="kingdom-mail__circle-btn kingdom-mail__send"
                    disabled={sending || !draft.trim()}
                    aria-label="Envoyer"
                  >
                    <Send aria-hidden="true" />
                  </button>
                </form>
              ) : isReadonlyKingdomThread(selected?.kind) ? null : (
                <p className="kingdom-mail__hint">
                  {selected && selected.canSend === false
                    ? "L'envoi de messages vous a été retiré."
                    : "Choisissez une conversation."}
                </p>
              )}
            </div>
          </div>
        </section>
      ) : null}
    </div>,
    portalRoot
  );
}
